import React from 'react';
import Select from 'react-select';
import { sports } from './enum';
import { sidSelectStyles } from './sidSelectStyles';

/*
 * Gli sport di un prodotto (corso, abbonamento, lezione, impianto). Dal 13/10/2026 un prodotto
 * puo' averne piu' d'uno, tra quelli dichiarati dalla struttura: il backend manda `sportTypes`
 * (tutti) e `sportType` (il principale, il primo dell'elenco). Il prodotto vale per tutti i suoi
 * sport: compare nei filtri di ciascuno e da' il prezzo da tesserato con la tessera di uno qualsiasi.
 * I prodotti di prima hanno solo `sportType`.
 */

/** Tutti gli sport del prodotto, come codici. */
export const sportDelProdotto = (prodotto) => {
    if (!prodotto) return [];
    if (Array.isArray(prodotto.sportTypes) && prodotto.sportTypes.length) return [...new Set(prodotto.sportTypes)];
    return prodotto.sportType ? [prodotto.sportType] : [];
};

/** Il prodotto e' di questo sport (per i filtri delle liste)? */
export const prodottoDelloSport = (prodotto, sport) => sportDelProdotto(prodotto).includes(sport);

/** I due campi da mandare al backend: il primo sport e' il principale. */
export const sportDaSalvare = (elenco) => ({
    sportType: elenco?.[0] || '',
    sportTypes: elenco || [],
});

/** I nomi degli sport del prodotto (o della tessera), separati da virgola («Tennis, Padel»). */
export const nomiSport = (prodotto) =>
    sportDelProdotto(prodotto)
        .map(codice => sports.find(s => s.value === codice)?.label || codice)
        .join(', ');

/*
 * Tessere (15/10/2026): una tessera copre tutti gli sport del prodotto che l'ha emessa (o quelli scelti
 * nell'inserimento manuale), con un solo numero e un solo organismo. Stessi campi dei prodotti:
 * `sportTypes` (tutti) e `sportType` (il primo). Le tessere di prima hanno solo `sportType`.
 * Per i nomi vale `nomiSport(tessera)`.
 */

/** Tutti gli sport della tessera, come codici. */
export const sportDellaTessera = (tessera) => sportDelProdotto(tessera);

/**
 * Gli organismi che la struttura ha indicato per TUTTI gli sport dell'elenco (club.sports[].sportPromotionBody):
 * un prodotto o una tessera fa capo a un solo organismo, che deve valere per ciascuno dei suoi sport.
 */
export const organismiComuni = (elencoSport, sportStruttura) => {
    if (!elencoSport?.length) return [];
    const perSport = elencoSport.map(codice => (sportStruttura || []).find(s => s.sportType === codice)?.sportPromotionBody || []);
    return [...new Set(perSport[0])].filter(organismo => perSport.every(elenco => elenco.includes(organismo)));
};

/** Le voci della tendina Organismo ({ value, label }) per i codici dati, nell'ordine dell'elenco degli organismi. */
export const opzioniOrganismi = (federations, codici) =>
    (federations || [])
        .filter(fed => codici.includes(fed.code))
        .map(fed => ({ value: fed.code, label: `${fed.code} - ${fed.description}` }));

/**
 * Il campo «Sport» dei prodotti: scelta multipla fra gli sport della struttura (`opzioni`, nel
 * formato { value, label } di `sports`). `valore` e `onChange` lavorano con l'elenco dei codici.
 */
export function CampoSport({ inputId, valore, onChange, opzioni, isDisabled = false, className = 'mt-2' }) {
    const scelti = (valore || []).map(codice => opzioni.find(o => o.value === codice) || { value: codice, label: codice });
    return (
        <Select
            className={className}
            styles={sidSelectStyles}
            placeholder=""
            inputId={inputId}
            isMulti
            isClearable={false}
            isDisabled={isDisabled}
            closeMenuOnSelect={false}
            value={scelti}
            onChange={(scelte) => onChange((scelte || []).map(s => s.value))}
            options={opzioni}
            noOptionsMessage={() => 'Nessun altro sport della struttura'}
        />
    );
}
